import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Code, Smartphone, Cloud, Search, Layout, Database, Terminal, Settings } from 'lucide-react';

const Services = () => {
    const services = [
        {
            icon: <Code size={32} />,
            title: 'Web Development',
            description: 'Custom web applications built with modern frameworks like React and Next.js for high performance.',
            features: ['Single Page Apps', 'E-commerce Solutions', 'Progressive Web Apps']
        },
        {
            icon: <Smartphone size={32} />,
            title: 'Mobile App Dev',
            description: 'Native and cross-platform mobile apps for iOS and Android that users love to interact with.',
            features: ['React Native', 'Flutter', 'iOS/Android Native']
        },
        {
            icon: <Database size={32} />,
            title: 'Backend Systems',
            description: 'Robust, secure, and scalable server-side architectures to power your complex business logic.',
            features: ['Node.js', 'Python/Django', 'API Integration']
        },
        {
            icon: <Cloud size={32} />,
            title: 'Cloud Solutions',
            description: 'Cloud infrastructure setup, migration, and management for maximum uptime and scalability.',
            features: ['AWS / Azure / GCP', 'DevOps Automation', 'Serverless']
        },
        {
            icon: <Layout size={32} />,
            title: 'UI/UX Design',
            description: 'User-centric design focus ensuring your products are not only functional but beautiful.',
            features: ['Interface Design', 'User Research', 'Prototyping']
        },
        {
            icon: <Search size={32} />,
            title: 'Digital Strategy',
            description: 'Strategic planning to align your digital products with your core business objectives.',
            features: ['SEO Optimization', 'Product Strategy', 'Growth Planning']
        }
    ];

    return (
        <div className="services-container">
            <section className="services-hero">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="section-header"
                    >
                        <h1 className="section-title">Our Solutions</h1>
                        <p className="section-subtitle">We offer a wide range of technology services tailored to your business.</p>
                    </motion.div>

                    <div className="services-grid">
                        {services.map((service, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="service-card glass-morphism"
                            >
                                <div className="service-icon-box">
                                    {service.icon}
                                </div>
                                <h3>{service.title}</h3>
                                <p className="service-desc">{service.description}</p>
                                <ul className="feature-list">
                                    {service.features.map((f, j) => (
                                        <li key={j}>
                                            <Terminal size={14} className="terminal-icon" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <div className="service-footer">
                                    <button className="details-btn">
                                        Details <Settings size={16} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="cta-section">
                <div className="container">
                    <div className="cta-wrapper glass-morphism">
                        <h2>Ready to start your project?</h2>
                        <p>Let's discuss how our expertise can bring your vision to life.</p>
                        <Link to="/contact" className="btn btn-primary cta-btn">Talk To Our Experts</Link>
                    </div>
                </div>
            </section>

            <style>{`
        .services-container { padding-top: 100px; }
        .services-hero { padding: 80px 0; }
        .section-header { text-align: center; margin-bottom: 6rem; }
        .section-title { font-size: 3.5rem; font-weight: 800; margin-bottom: 1.5rem; }
        .section-subtitle { font-size: 1.25rem; color: var(--text-muted); max-width: 700px; margin: 0 auto; }
        
        .services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 2.5rem; }
        .service-card { padding: 3.5rem; border-radius: 3rem; border: 1px solid var(--border-glass); transition: var(--transition); position: relative; overflow: hidden; display: flex; flex-direction: column; }
        .service-card:hover { transform: translateY(-15px); border-color: var(--primary); background: rgba(0, 242, 255, 0.03); }
        
        .service-icon-box { width: 70px; height: 70px; background: rgba(0, 242, 255, 0.1); border-radius: 1.5rem; display: flex; align-items: center; justify-content: center; color: var(--primary); margin-bottom: 2.5rem; transition: var(--transition); }
        .service-card:hover .service-icon-box { transform: scale(1.1) rotate(5deg); background: var(--primary); color: #000; }
        
        .service-card h3 { font-size: 1.8rem; margin-bottom: 1.2rem; font-weight: 700; }
        .service-desc { color: var(--text-muted); margin-bottom: 2.5rem; font-size: 1.1rem; line-height: 1.7; flex-grow: 1; }
        
        .feature-list { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 3rem; }
        .feature-list li { display: flex; align-items: center; gap: 0.8rem; font-size: 0.95rem; color: #ddd; font-weight: 500; }
        .terminal-icon { color: var(--primary); }
        
        .details-btn { border: none; background: transparent; color: var(--primary); font-weight: 800; display: flex; align-items: center; gap: 0.6rem; font-size: 1rem; padding: 0; }
        
        .cta-section { padding: 120px 0; }
        .cta-wrapper { padding: 6rem 4rem; border-radius: 4rem; text-align: center; background: linear-gradient(135deg, rgba(124, 77, 255, 0.1), rgba(0, 242, 255, 0.1)); border: 1px solid var(--border-glass); }
        .cta-wrapper h2 { font-size: 3.5rem; font-weight: 800; margin-bottom: 1.5rem; }
        .cta-wrapper p { font-size: 1.25rem; color: var(--text-muted); margin-bottom: 3.5rem; }
        .cta-btn { padding: 1.2rem 4rem; font-size: 1.2rem; }

        @media (max-width: 1024px) {
          .services-grid { grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
          .cta-wrapper h2 { font-size: 2.8rem; }
        }

        @media (max-width: 768px) {
          .services-hero { padding: 60px 0; }
          .section-title { font-size: 2.8rem; }
          .services-grid { grid-template-columns: 1fr; gap: 2rem; }
          .service-card { padding: 2.5rem 2rem; border-radius: 2.5rem; }
          .cta-section { padding: 80px 0; }
          .cta-wrapper { padding: 4rem 2rem; border-radius: 3rem; }
          .cta-wrapper h2 { font-size: 2.2rem; }
          .cta-wrapper p { font-size: 1.1rem; }
        }
      `}</style>
        </div>
    );
};

export default Services;
